import { Icon, Label } from "@/shared/components";
import { cn } from "@/shared/utils";
import * as LabelPrimitive from "@radix-ui/react-label";
import { Slot } from "@radix-ui/react-slot";
import * as React from "react";
import { Controller, ControllerProps, FieldPath, FieldValues, FormProvider } from "react-hook-form";
import { FormFieldContext, FormItemContext } from "./context";
import { useFormField } from "./use-form-field";

export const Form = FormProvider;

export function FormItem({ className, ...props }: React.ComponentProps<"div">) {
    const id = React.useId();

    return (
        <FormItemContext value={{ id }}>
            <div data-slot="form-item" className={cn("grid gap-1", className)} {...props} />
        </FormItemContext>
    );
}

export const FormField = <TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>>({
    ...props
}: ControllerProps<TFieldValues, TName>) => {
    return (
        <FormFieldContext value={{ name: props.name }}>
            <Controller {...props} />
        </FormFieldContext>
    );
};

export function FormLabel({
    className,
    optional,
    readOnly,
    ...props
}: React.ComponentProps<typeof LabelPrimitive.Root> & { optional?: boolean; readOnly?: boolean }) {
    const { error, formItemId } = useFormField();

    return (
        <Label
            data-slot="form-label"
            data-error={!!error}
            className={cn("data-[error=true]:text-destructive", className)}
            optional={optional}
            readOnly={readOnly}
            htmlFor={formItemId}
            {...props}
        />
    );
}

export function FormControl({ ...props }: React.ComponentProps<typeof Slot>) {
    const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

    return (
        <Slot
            data-slot="form-control"
            id={formItemId}
            aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`}
            aria-invalid={!!error}
            {...props}
        />
    );
}

export function FormDescription({ className, ...props }: React.ComponentProps<"p">) {
    const { formDescriptionId } = useFormField();

    return <p data-slot="form-description" id={formDescriptionId} className={cn("text-muted-foreground text-s", className)} {...props} />;
}

export function FormMessage({ className, ...props }: React.ComponentProps<"p">) {
    const { error, formMessageId } = useFormField();
    const body = error ? String(error?.message ?? "") : props.children;

    if (!body) {
        return null;
    }

    return (
        <FormErrorMessage className={className} formMessageId={formMessageId}>
            {body}
        </FormErrorMessage>
    );
}

type FormErrorMessageProps = React.ComponentProps<"p"> & { formMessageId?: string };

/**
 * Use FormErrorMessage directly for global/root errors (not tied to a field context).
 * Use FormMessage for field errors inside FormField/FormItem.
 */
export function FormErrorMessage({ children, className, formMessageId, ...props }: FormErrorMessageProps) {
    return (
        <p
            data-slot="form-message"
            id={formMessageId}
            className={cn("text-destructive text-s flex items-center gap-2", className)}
            {...props}
        >
            <Icon name="error-filled" size="xs" /> {children}
        </p>
    );
}
